package app.nestbridge.feature.invitations.ui

import androidx.compose.foundation.BorderStroke
import androidx.compose.foundation.background
import androidx.compose.foundation.clickable
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.automirrored.outlined.ArrowForwardIos
import androidx.compose.material.icons.outlined.QrCodeScanner
import androidx.compose.material3.HorizontalDivider
import androidx.compose.material3.Icon
import androidx.compose.material3.Surface
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.tooling.preview.Preview
import androidx.compose.ui.tooling.preview.PreviewParameter
import androidx.compose.ui.unit.dp
import com.karawek.core.ui.components.CoreScaffold
import com.karawek.core.ui.components.CoreSecondaryButton
import com.karawek.core.ui.theme.CoreShapes
import com.karawek.core.ui.theme.CoreSpacing
import com.karawek.core.ui.theme.CoreTheme
import com.karawek.core.ui.theme.CoreTypography
import com.karawek.core.ui.theme.ThemeMode
import com.karawek.core.ui.theme.ThemeModeProvider
import app.nestbridge.feature.invitations.model.Invitation
import app.nestbridge.feature.invitations.model.InvitationDeliveryMethod
import app.nestbridge.feature.invitations.model.InvitationStatus
import app.nestbridge.feature.invitations.model.OrganizationType
import app.nestbridge.feature.invitations.screen.InvitationListEvent
import app.nestbridge.feature.invitations.screen.InvitationListScreen
import app.nestbridge.feature.invitations.screen.InvitationListState
import app.nestbridge.ui.theme.NestBridgeTheme
import com.slack.circuit.codegen.annotations.CircuitInject
import software.amazon.lastmile.kotlin.inject.anvil.AppScope
import kotlin.time.Instant

@CircuitInject(
    screen = InvitationListScreen::class,
    scope = AppScope::class,
)
@Composable
fun InvitationListUi(
    state: InvitationListState,
    modifier: Modifier = Modifier,
) {
    CoreScaffold(
        title = "Your Invitations",
        subtitle = "These are requests to connect. Nothing is shared unless you accept.",
        actionText = if (state.isUserPartOfAnyTeam) "Skip" else null,
        onAction = if (state.isUserPartOfAnyTeam) { { state.eventSink(InvitationListEvent.DecideLater) } } else null,
        onAppear = { state.eventSink(InvitationListEvent.OnAppear) },
        footer = { Footer(state) },
        modifier = modifier,
    ) {
        Column(
            modifier = Modifier
                .fillMaxSize(),
            verticalArrangement = Arrangement.SpaceBetween,
        ) {
            Column(verticalArrangement = Arrangement.spacedBy(CoreSpacing.md)) {
                state.invitations.forEach { invitation ->
                    InvitationCard(
                        invitation = invitation,
                        onClick = {
                            state.eventSink(
                                InvitationListEvent.OpenInvitation(invitation.id),
                            )
                        },
                    )
                }
            }
        }
    }
}

@Composable
private fun Footer(state: InvitationListState) =
    Column(
        modifier = Modifier.fillMaxWidth(),
        verticalArrangement = Arrangement.spacedBy(CoreSpacing.sm),
    ) {
        HorizontalDivider(
            thickness = 1.dp,
            color = CoreTheme.colors.divider,
            modifier = Modifier
                .fillMaxWidth()
                .padding(bottom = CoreSpacing.sm),
        )

        Text(
            text = "Have a QR code? Scan it to review an invitation.",
            style = CoreTypography.caption,
            color = CoreTheme.colors.textSecondary,
            modifier = Modifier.align(Alignment.CenterHorizontally),
        )

        CoreSecondaryButton(
            leadingIcon = Icons.Outlined.QrCodeScanner,
            text = "Scan QR code",
            onClick = { state.eventSink(InvitationListEvent.ScanQr) },
            modifier = Modifier
                .fillMaxWidth()
                .padding(vertical = CoreSpacing.sm),
        )
    }


@Composable
private fun InvitationCard(
    invitation: Invitation,
    onClick: () -> Unit,
) {
    Surface(
        modifier = Modifier
            .fillMaxWidth()
            .clip(CoreShapes.lg)
            .clickable { onClick() },
        color = CoreTheme.colors.surface,
        border = BorderStroke(1.dp, CoreTheme.colors.divider),
    ) {
        Row(
            modifier = Modifier
                .fillMaxWidth()
                .padding(CoreSpacing.lg),
            horizontalArrangement = Arrangement.SpaceBetween,
            verticalAlignment = Alignment.CenterVertically,
        ) {
            Column(
                verticalArrangement = Arrangement.spacedBy(CoreSpacing.xs),
                modifier = Modifier.weight(1f),
            ) {
                Text(
                    text = invitation.childDisplayName,
                    style = CoreTypography.title,
                    color = CoreTheme.colors.textPrimary,
                )
                Text(
                    text = if (invitation.teamId != null) {
                        invitation.teamType?.label ?: "Care team"
                    } else {
                        "New care team"
                    },
                    style = CoreTypography.bodySecondary,
                    color = CoreTheme.colors.textSecondary,
                )
                Text(
                    text = invitedByLabel(invitation),
                    style = CoreTypography.caption,
                    color = CoreTheme.colors.textSecondary,
                )
            }

            Row(
                horizontalArrangement = Arrangement.spacedBy(CoreSpacing.sm),
                verticalAlignment = Alignment.CenterVertically,
            ) {
                StatusPill(invitation.status)
                Icon(
                    imageVector = Icons.AutoMirrored.Outlined.ArrowForwardIos,
                    contentDescription = null,
                    tint = CoreTheme.colors.textSecondary,
                )
            }
        }
    }
}

@Composable
private fun StatusPill(status: InvitationStatus) {
    Box(
        modifier = Modifier
            .clip(RoundedCornerShape(12.dp))
            .background(CoreTheme.colors.surfaceVariant)
            .padding(horizontal = CoreSpacing.xs, vertical = 6.dp),
    ) {
        Text(
            text = when (status) {
                InvitationStatus.PENDING -> "Pending"
                InvitationStatus.ACCEPTED -> "Accepted"
                InvitationStatus.DECLINED -> "Declined"
                InvitationStatus.EXPIRED -> "Expired"
            },
            style = CoreTypography.caption,
            color = CoreTheme.colors.textMuted,
        )
    }
}

private fun invitedByLabel(invitation: Invitation): String {
    val organization = invitation.inviterOrganizationName?.takeIf { it.isNotBlank() }
    return if (organization != null) {
        "Invited by ${invitation.inviterDisplayName} · $organization"
    } else {
        "Invited by ${invitation.inviterDisplayName}"
    }
}

@Preview
@Composable
private fun InvitationListPreview(
    @PreviewParameter(ThemeModeProvider::class) themeMode: ThemeMode,
) {
    NestBridgeTheme(themeMode = themeMode) {
        InvitationListUi(
            state = InvitationListState(
                invitations = listOf(
                    Invitation(
                        id = "1",
                        status = InvitationStatus.PENDING,
                        inviterUserProfileId = "person_001",
                        inviterDisplayName = "Dr. Sarah Chen",
                        inviterOrganizationName = "Metro Early Intervention",
                        inviterOrganizationType = OrganizationType.EARLY_INTERVENTION,
                        childId = "child_01",
                        childDisplayName = "Alex",
                        teamId = "team_01",
                        teamType = app.nestbridge.domain.identity.model.ProfessionalServiceType.SpeechLanguageTherapy,
                        proposedTeamRole = app.nestbridge.feature.careteam.model.TeamRelationshipRole.THERAPIST,
                        grantsChildStewardship = false,
                        deliveryMethod = InvitationDeliveryMethod.EMAIL,
                        deliveryDestination = "parent@example.com",
                        createdAt = Instant.fromEpochSeconds(1_713_000_000),
                        createdBy = "person_001",
                        modifiedAt = Instant.fromEpochSeconds(1_713_000_000),
                        modifiedBy = "person_001",
                    ),
                    Invitation(
                        id = "2",
                        status = InvitationStatus.PENDING,
                        inviterUserProfileId = "person_002",
                        inviterDisplayName = "Ava Martinez",
                        inviterOrganizationName = null,
                        inviterOrganizationType = null,
                        childId = "child_02",
                        childDisplayName = "Jamie",
                        teamId = null,
                        teamType = null,
                        proposedTeamRole = app.nestbridge.feature.careteam.model.TeamRelationshipRole.ORGANIZER,
                        grantsChildStewardship = true,
                        deliveryMethod = InvitationDeliveryMethod.EMAIL,
                        deliveryDestination = "ava.martinez@example.com",
                        createdAt = Instant.fromEpochSeconds(1_713_200_000),
                        createdBy = "person_002",
                        modifiedAt = Instant.fromEpochSeconds(1_713_200_000),
                        modifiedBy = "person_002",
                    ),
                ),
                isUserPartOfAnyTeam = true,
                eventSink = {},
            ),
        )
    }
}
